import cookie from 'cookie';
import { FC, useEffect } from 'react';
import type { GetServerSideProps } from 'next';
import { useSelector } from 'react-redux';

import type { Page } from 'src/__generated__/graphqlTypes';
import { getSimplePage } from 'src/api/pageApi';
import { wrapper } from 'src/redux/store/store';
import { setPageData, setPages } from 'app-redux/actions/pageActions';
import { calculateCustomerType } from 'lib/session.service';
import type { RightPsychic, ServerSideRenderingContext } from 'types/objectTypes';
import { setAuthenticatedState } from 'app-redux/actions/authActions';
import { setOAPlatform } from 'app-redux/actions/appActions';
import type { Store } from 'app-redux/types/storeTypes';
import { CustomerType } from 'constants/enums';
import { recognizeOAPlatform } from 'lib/page.service';
import withMiddleware, { commonMiddleware, postCommonMiddleware } from 'src/shared/lib/middleware/combiner';
import { setDefaultCacheBehavior } from 'lib/storage.service';
import { parsePsychicsUrl } from 'lib/psychic.service';
import { getPsychicRoute } from 'pages/api/psychics/[id]';
import { getAndMapPsychicWithTestimonials } from 'pages/api/psychics/testimonials';
import { returnValueIfExists } from 'lib/server.service';
import { BIO_SLUG } from 'constants/constants';
import { setSessionWithHashedId } from 'src/shared/lib/server/auth';
import { Exponea } from 'lib/external/exponea';
import { buildPersonSchema, PsychicBioSchemaScript } from 'lib/psychicBioSchema.service';

const handler = async ({
  // @ts-ignore
  params,
  req,
  path,
  res,
  store,
  preview = false,
  query,
}: ServerSideRenderingContext): Promise<any> => {
  const session = req?.session?.getSession();
  const customerType = calculateCustomerType(req.session);

  const isAuthorized = customerType === CustomerType.EC;
  try {
    const slugFromParams = params?.slug || { slug: '' };
    const slug = (slugFromParams as string).toLowerCase();

    if (!slug) {
      return { notFound: true };
    }

    const { extId } = parsePsychicsUrl(slug);

    let page: Page | undefined;

    if (extId) {
      const pagePromise = getSimplePage({ slug: BIO_SLUG, customerType, preview });
      const psychicPromise = getPsychicRoute(extId, session, req.headers.cookie);
      const testimonialPromise = getAndMapPsychicWithTestimonials(
        { extId, pageIndex: 0, pageSize: 10 },
        session,
      );
      const [
        bioPage,
        psychic,
        testimonials,
      ] = await Promise.all([pagePromise, psychicPromise, testimonialPromise]);
      page = bioPage;

      if (!psychic) {
        return { notFound: true };
      }

      psychic.skipFbSetEwt = true;

      if (!page) {
        return { notFound: true };
      }

      const extIdsCookie = req.cookies.extIds || '[]';
      const extIdsArray = JSON.parse(extIdsCookie).filter(
        (cookieId: number) => cookieId !== parseInt(extId, 10),
      );
      extIdsArray.push(parseInt(extId, 10));

      try {
        res.setHeader('Set-Cookie', cookie.serialize('extIds', JSON.stringify(extIdsArray), {
          secure: process.env.NODE_ENV === 'production',
          path: '/',
        }));

        const { promocode } = query;

        if (promocode) {
          res.setHeader('Set-Cookie', cookie.serialize('offerpromoCode', promocode as string, {
            secure: process.env.NODE_ENV === 'production',
            path: '/',
          }));
        }
      } catch (error) {
        console.error('Error setting cookies:', error);
      }

      if (page.seo) {
        const base = process.env.BASE_SERVER_URL || process.env.NEXT_PUBLIC_BASE_SERVER_URL;
        const pageUrl = `${base}/psychics/${psychic.lineName?.toLowerCase()}-${extId}`;
        page.seo.schema = buildPersonSchema(psychic, pageUrl);
      }

      store.dispatch(setPages<Page>({ key: BIO_SLUG, value: page }));

      try {
        store.dispatch(setPageData({
          extId,
          psychic,
          testimonials,
        }));
      } catch (e) {
        console.error(e);
      }
    } else {
      page = await getSimplePage({ slug, customerType, preview, path });

      if (!page) {
        return { notFound: true };
      }

      store.dispatch(setPages<Page>({ key: slug, value: page }));
    }

    store.dispatch(setAuthenticatedState(isAuthorized));
    setSessionWithHashedId(store, session);
    const oaPlatform = recognizeOAPlatform(req.headers);
    store.dispatch(setOAPlatform(oaPlatform));
    setDefaultCacheBehavior(120, res);

    return { props: { } };
  } catch (e) {
    console.error(e);

    return { props: {} };
  }
};

export const getServerSideProps: GetServerSideProps = wrapper
// @ts-ignore
  .getServerSideProps(withMiddleware(
    handler,
    { prior: commonMiddleware, post: postCommonMiddleware },
  ));

const Component: FC = () => {
  const data = useSelector((store: Store) => store.server.page.data);
  const user = useSelector((store: Store) => store.server.auth.user);
  const platform = useSelector((store: Store) => store.server.app.oaPlatform);

  const psychic = data.psychic as RightPsychic | undefined;
  const { extId } = data;

  /**
   * Dependencies are empty because data will go directly from server
   * do not do it in different places
   *  */
  useEffect(() => {
    if (!psychic || !extId) {
      return;
    }

    const obj = {
      BrandName: 'CP',
      FullRate: psychic.basePrice.replace('$', ''),
      Platform: platform,
      PsychicID: extId,
      PsychicName: psychic.lineName,
      ...returnValueIfExists('CustomerID', user?.customerId),
      ...returnValueIfExists('PsychicCircle', psychic.isInCircle || false),
      ...returnValueIfExists('Favorite', psychic.isFavorite || false),
    };

    Exponea.track('product_view', obj);
  }, []);

  return <PsychicBioSchemaScript />;
};

export default Component;
